import type { User } from '@quotetome/materials-api'
import { defineStore } from 'pinia'

import { api } from 'boot/materials-api'
import { oneSignal } from 'boot/one-signal'
import { sentry } from 'boot/sentry'
import { AUTH_VERSION } from 'app/constants'

function userHasPerm(user?: User, permCodename?: string) {
  return user && user.qtm_permissions?.some(perm => perm.codename === permCodename)
}

interface State {
  authToken: string | undefined,
  environment: 'dev' | 'staging' | 'prod' | 'demo',
  user: User | undefined,
}

export default defineStore('auth', {
  state: (): State => ({
    authToken: undefined,
    environment: process.env.QTM_ENV as State['environment'],
    user: undefined,
  }),
  getters: {
    allowCustomCostCodes(state) {
      return state.user?.company?.allow_custom_cost_codes
    },

    canCallVendor(state) {
      return userHasPerm(state.user, 'qtm_can_cyb')
    },

    canCreateInStorePO(state) {
      return userHasPerm(state.user, 'qtm_can_create_in_store_po')
    },

    canCreateJobsite(state) {
      return userHasPerm(state.user, 'qtm_can_create_jobsite')
    },

    canCreateChangeOrder(state) {
      return userHasPerm(state.user, 'qtm_can_create_change_order')
    },

    canCreatePO(state) {
      return userHasPerm(state.user, 'qtm_can_create_purchase_order')
    },

    canCreateQuickPO() {
      return (this.canCreatePO as any) && !(this.canCreateInStorePO as any)
    },

    canGetQuotes(state) {
      return userHasPerm(state.user, 'qtm_can_get_quotes')
    },

    canManageCompany(state) {
      return userHasPerm(state.user, 'qtm_can_manage_company')
    },

    canManageInventory(state) {
      return userHasPerm(state.user, 'qtm_can_use_inventory')
    },

    canUseBom(state) {
      return userHasPerm(state.user, 'qtm_can_use_bom')
    },

    canUseExpenses(state) {
      return userHasPerm(state.user, 'qtm_can_use_expenses')
    },

    canUseRequisitions(state) {
      return userHasPerm(state.user, 'qtm_can_use_requisitions')
    },

    canViewInventory(state) {
      return userHasPerm(state.user, 'qtm_can_view_inventory')
    },

    isAuthenticated(state) {
      return !!state.authToken && !!state.user
    },

    isProduction(state) {
      return state.environment === 'prod'
    },

    isStaff(state) {
      return state.user?.is_staff
    },

    purchasingEmail(state) {
      return state.user && state.user.company ? state.user.company.purchasing_email : ''
    },

    userCompany(state) {
      return state.user ? state.user.company : undefined
    },

    isUserCompanyInSetupMode(state) {
      return state.user && state.user.company ? state.user.company.setup_mode : false
    },

    userCountry(state) {
      return state.user?.company?.country
    },

    hasPurchasingAuthority(state) {
      return (amount: number) => {
        if (state.user?.purchasing_limit == null) {
          return true
        }

        const limit = Number(state.user.purchasing_limit)

        if (limit === 0) {
          return false
        }

        return limit >= amount
      }
    },

    showJoin(state) {
      return state.user?.company?.allow_users_to_join_projects
    },

    signatureOptional(state) {
      return state.user?.company!.signature_required === 2
    },

    signatureRequired(state) {
      return state.user?.company!.signature_required === 1
    },
  },
  actions: {
    async configurePushNotifications(user?: User, environment?: State['environment']) {
      if (oneSignal.initialized && user && user.id) {
        oneSignal.optIn(user.id, environment)

        const devicePermission = await oneSignal.devicePermission()

        if (user.allow_push !== devicePermission) {
          (api as any).v1.users.me.patch({ allow_push: devicePermission })
          user.allow_push = devicePermission
        }
      }
    },

    setAuthToken(token?: string) {
      if (token) {
        localStorage.setItem('authToken', token)
        localStorage.setItem('authVersion', AUTH_VERSION)
      }
      else {
        localStorage.removeItem('authToken')
      }

      this.authToken = token
    },

    setEnvironment(environment: State['environment']) {
      this.environment = environment
    },

    setUser(user: User) {
      this.user = user
      this.configurePushNotifications(user, this.environment)

      if (user) {
        sentry.setUser({ email: user.email })
      }
      else {
        sentry.setUser(null)
      }
    },

    updateUser(changes: any) {
      Object.assign(this.user as any, changes)
    }

  }
})
